import React, { useState, useMemo } from 'react';
import { ConfigProvider, Flex, Tag, Tooltip, type ThemeConfig } from 'antd';
import {
  BatteryIcon,
  CellSignalIcon,
  HumidityIcon,
  LightIcon,
  PressureIcon,
  ShockIcon,
  SignalIcon,
  TempratureRangeIcon,
  TiltIcon,
  VibilityWindowIcon,
} from '../../../assets/icons/icons';
import { changeToTimeString } from '../../../common/helper/appHelper';
import { sortSensorsByPriorityAndId } from '../../Modals/utils/chartUtils';
import {
  getSensorVitalsLayout,
  type SensorVitalsLayoutCase,
} from './sensorVitalsLayout';
import {
  formatVitalDisplay,
  getLatestVitalValue,
  getCellSignalRings,
  getShareHiddenVitals,
  getTagStyleForVital,
  getVitalDisplayUnit,
  hasVitalData,
  VITAL_ORDER,
  VITAL_TAG_LABELS,
  ALWAYS_SHOW_VITALS,
  type TagStyles,
  type VitalModalType,
} from './sensorVitals';
import { SingleShipmentDataExtended } from '../../../apis/types';
import { getUserTimeZone, parseShipmentDayjs } from '@/common/utils/utils';
import { useTelemetrySummary } from '../SensorTimeline/queries';
import SensorTimelineModal from '../SensorTimeline/SensorTimelineModal';
import { availableCharts } from '../SensorTimeline/lib/measures';
import {
  findSensor,
  sensorReadings,
  summaryVital,
} from '../SensorTimeline/lib/summaryVitals';

interface PointState {
  temperature?: number | string;
  battery?: number | string;
  humidity?: number | string;
  pressure?: number | string;
  light?: number | string;
  tilt?: number | string;
  shock_value?: number | string;
  cell_signal?: number | string;
  lat_updated?: string;
}

interface ShipmentVitals_CTProps {
  shipment?: Partial<SingleShipmentDataExtended>;
  openCharts?: boolean;
  pointState?: PointState;
  shareToken?: string;
  layoutCase?: SensorVitalsLayoutCase;
  /** Rendered in the same row as vital tags (e.g. time remaining). Used only for case 3 (single_many) so layout stays self-contained. */
  trailing?: React.ReactNode;
}

interface SensorData {
  sensorId: string;
  sensorLabel: string;
  priority?: number;
  latestReading: SingleShipmentDataExtended['monitor_info'][number] | null;
}

const VITAL_ICONS: Record<VitalModalType, React.ReactNode> = {
  temperature: <TempratureRangeIcon />,
  battery: <BatteryIcon />,
  light: <LightIcon />,
  humidity: <HumidityIcon />,
  tilt: <TiltIcon />,
  pressure: <PressureIcon />,
  shock_value: <ShockIcon />,
  cell_signal: <CellSignalIcon rings={3} />,
};

/** Widens the vital tooltip past antd's 250px default so a reading time fits on one line. */
const VITAL_TOOLTIP_THEME: ThemeConfig = {
  components: { Tooltip: { maxWidth: 400 } },
};

/** Single vital tag (tooltip + tag) to avoid repeating the same structure for each vital type. */
const VitalTag: React.FC<{
  tooltipLabel: string;
  timestamp: string;
  readingLines?: string[];
  style: TagStyles;
  onClick?: () => void;
  disabled?: boolean;
  children: React.ReactNode;
  icon: React.ReactNode;
}> = ({
  tooltipLabel,
  timestamp,
  readingLines,
  style,
  onClick,
  disabled,
  children,
  icon,
}) => (
  <ConfigProvider theme={VITAL_TOOLTIP_THEME}>
    <Tooltip
      placement='top'
      title={
        <p className='fw-semibold'>
          <span>{tooltipLabel}</span>
          {readingLines ? (
            readingLines.map((line) => (
              <React.Fragment key={line}>
                <br />
                {line}
              </React.Fragment>
            ))
          ) : timestamp ? (
            <>
              <br />
              {`Last ping: ${timestamp}`}
            </>
          ) : null}
        </p>
      }
      arrow={true}
    >
      <Tag
        icon={icon}
        className='vitals-custom-ant-tag'
        style={{
          ...style,
          ...(disabled ? { cursor: 'not-allowed' } : { cursor: 'pointer' }),
        }}
        onClick={disabled ? undefined : onClick}
      >
        {children}
      </Tag>
    </Tooltip>
  </ConfigProvider>
);

/**
 * Component that displays shipment vitals as colored tags with sensor readings
 *
 * @param shipment - Shipment data containing product limits and monitor info
 * @param openCharts - Whether clicking tags should open modal charts
 * @param pointState - Current sensor state from real-time updates
 */
const ShipmentVitals_CT: React.FC<ShipmentVitals_CTProps> = ({
  shipment,
  openCharts,
  pointState = {},
  layoutCase: layoutCaseProp,
  trailing,
  shareToken,
}) => {
  const layoutCase =
    layoutCaseProp ?? getSensorVitalsLayout(shipment).layoutCase;

  const { data: summary } = useTelemetrySummary(shipment?.id, {
    enabled: true,
    shareToken,
  });

  const fallbackSingleSensorId = useMemo(() => {
    const summarySensorId = shipment?.sensor_summary?.[0]?.sensor_id;
    if (summarySensorId) return summarySensorId;
    const sensors = Array.isArray(shipment?.sensors) ? shipment.sensors : [];
    if (sensors.length > 0 && sensors[0]?.id) return sensors[0].id;
    return null;
  }, [shipment?.sensor_summary, shipment?.sensors]);

  const shipmentReported = summary
    ? availableCharts(summary).length > 0
    : false;

  const [timelineVital, setTimelineVital] = useState<VitalModalType | null>(
    null
  );
  // Share links: chips for vitals the sender hid are left out, even always-shown ones.
  const hiddenVitals = getShareHiddenVitals(shipment);

  const handleOpenModal = (type: VitalModalType): void => {
    if (openCharts) setTimelineVital(type);
  };

  // Build sensor list from sensor_summary and attach latest monitor_info reading.
  const groupedSensors = useMemo(() => {
    const summary = shipment?.sensor_summary ?? [];
    if (!summary.length) {
      return [];
    }
    const infoPoints = shipment?.monitor_info ?? [];

    return summary
      .map((s) => ({
        sensorId: s.sensor_id ?? '',
        sensorLabel: s.sensor_name ?? '',
        priority: s.priority,
        latestReading:
          infoPoints.find((p) => p.shipment_sensor_id === s.sensor_id) ?? null,
      }))
      .filter((sensor) => sensor.sensorId && sensor.sensorLabel)
      .sort((a, b) =>
        sortSensorsByPriorityAndId(
          { priority: a.priority, id: a.sensorId },
          { priority: b.priority, id: b.sensorId }
        )
      );
  }, [shipment?.sensor_summary, shipment?.monitor_info]);

  // Render vitals tags for a specific sensor reading
  const renderSensorVitals = (
    sensorData: SensorData | null,
    isPrimary: boolean = false
  ) => {
    const reading = sensorData?.latestReading;
    const monitorInfoFirst = shipment?.monitor_info?.[0];
    const timestamp = reading?.lat_updated || monitorInfoFirst?.timestamp || '';

    const sources = {
      shipment: shipment ?? {},
      sensorData: sensorData ? { sensorId: sensorData.sensorId } : null,
      pointState,
      isPrimary,
    };

    const vitalSources = {
      shipment: shipment ?? {},
      sensorData: sensorData ? { sensorId: sensorData.sensorId } : null,
    };

    const summarySensor = summary
      ? findSensor(summary, sensorData?.sensorId ?? fallbackSingleSensorId)
      : null;
    const readings =
      summary && summarySensor
        ? sensorReadings(summarySensor, summary.anchors.delivered !== null)
        : null;
    const selectedPoint =
      isPrimary && pointState?.lat_updated ? pointState : null;
    const timeZone = getUserTimeZone().name;
    const fromSummary = (field: VitalModalType) =>
      readings ? summaryVital(field, readings, selectedPoint, timeZone) : null;

    const summaryDrivesValues = readings !== null;
    const getValue = (field: VitalModalType) =>
      summaryDrivesValues
        ? fromSummary(field)?.value
        : getLatestVitalValue(field, sources);
    const hasData = (field: VitalModalType) =>
      summaryDrivesValues
        ? Boolean(fromSummary(field)?.hasData)
        : hasVitalData(field, vitalSources);

    return (
      <Flex gap={8} style={{ flexWrap: 'wrap' }}>
        {VITAL_ORDER.filter((type) => !hiddenVitals.includes(type)).map(
          (type) => {
            const vitalHasData = hasData(type);
            const alwaysShow = ALWAYS_SHOW_VITALS.includes(type);
            if (!alwaysShow && !vitalHasData) return null;
            const opensModal = vitalHasData || shipmentReported;
            return (
              <VitalTag
                key={type}
                tooltipLabel={VITAL_TAG_LABELS[type]}
                timestamp={timestamp}
                readingLines={fromSummary(type)?.readingLines}
                style={getTagStyleForVital(type, shipment?.product, getValue)}
                disabled={!opensModal}
                onClick={opensModal ? () => handleOpenModal(type) : undefined}
                icon={
                  type === 'cell_signal' ? (
                    <CellSignalIcon
                      rings={getCellSignalRings(getValue(type))}
                    />
                  ) : (
                    VITAL_ICONS[type]
                  )
                }
              >
                {formatVitalDisplay(
                  getValue(type),
                  '--',
                  type === 'shock_value' ? 2 : 0
                )}
                {getVitalDisplayUnit(type, shipment)}
              </VitalTag>
            );
          }
        )}
      </Flex>
    );
  };

  const modals = timelineVital && shipment?.id && (
    <SensorTimelineModal
      shipmentId={shipment.id}
      shareToken={shareToken}
      shipment={shipment}
      focus={timelineVital}
      onClose={() => setTimelineVital(null)}
    />
  );

  // Case 2 (inline): vitals on same row as "Shipment details"
  if (layoutCase === 'inline') {
    return (
      <>
        <Flex
          align='center'
          gap={8}
          data-layout='inline'
          className='shipment-inline-vitals-row'
          style={{ flexShrink: 0, maxWidth: '100%' }}
        >
          {renderSensorVitals(null, true)}
        </Flex>
        {modals}
      </>
    );
  }

  return (
    <>
      <Flex
        vertical
        gap={12}
        data-layout={layoutCase}
        style={{ width: '100%' }}
      >
        {layoutCase === 'multi' ? (
          // Multiple sensors (2+): render each sensor in its own row with name on left, vitals end-aligned right
          groupedSensors.map((sensorData) => (
            <Flex
              key={sensorData.sensorId}
              align='center'
              gap={12}
              className='shipment-multi-sensor-row'
              style={{ width: '100%' }}
            >
              <Flex align='center' gap={8} style={{ flexShrink: 0 }}>
                <SignalIcon width={18} height={18} color='var(--gray-600)' />
                <div className='shipment-vitals-sensor-label'>
                  {sensorData.sensorLabel}
                </div>
              </Flex>
              <div className='shipment-multi-sensor-vitals'>
                {renderSensorVitals(sensorData, sensorData.priority === 1)}
              </div>
            </Flex>
          ))
        ) : (
          // Case 3 (single_many): vitals + trailing in one Flex so they stay grouped (same gap as between tags)
          <Flex
            className='shipment-single-many-vitals-row'
            align='center'
            justify='flex-start'
            gap={8}
            style={{ width: '100%', flexWrap: 'wrap' }}
          >
            <div className='shipment-single-many-vitals-tags'>
              {renderSensorVitals(null, true)}
            </div>
            {trailing && (
              <div className='shipment-single-many-vitals-trailing'>
                {trailing}
              </div>
            )}
          </Flex>
        )}
      </Flex>

      {modals}
    </>
  );
};

// Export separate component for viability window tag
export const ViabilityWindowTag: React.FC<{
  shipment?: Partial<SingleShipmentDataExtended>;
}> = ({ shipment }) => {
  const getVibilityWindowStyles = (
    current: string | undefined,
    status: string | undefined,
    scheduled_delivery_date: string | undefined
  ): TagStyles => {
    if (!current || current === '00:00:00') {
      return {
        backgroundColor: 'var(--light-gray)', // light gray
        color: 'var(--sensor-neutral-text)', // dark gray
      };
    }

    const now = new Date();
    const scheduled = parseShipmentDayjs(scheduled_delivery_date);
    const scheduledDate = scheduled?.toDate();

    const isDelayedAndDelivered =
      status?.toLowerCase() !== 'delivered' &&
      Boolean(scheduledDate) &&
      now > scheduledDate!;

    if (isDelayedAndDelivered) {
      return {
        backgroundColor: 'var(--danger-thin)', // light red
        color: 'var(--sensor-danger-text)', // red
      };
    }

    return {
      backgroundColor: 'var(--thin-green)', // light green
      color: 'var(--green-900)', // green
    };
  };

  return (
    <Tooltip
      placement='top'
      title={
        <p
          className='fw-semibold'
          dangerouslySetInnerHTML={{
            __html: '<span>Viable transit time remaining</span>',
          }}
        />
      }
      arrow={true}
    >
      <Tag
        icon={<VibilityWindowIcon />}
        className='vitals-custom-ant-tag'
        style={{
          ...getVibilityWindowStyles(
            shipment?.schedule_viability_window,
            shipment?.status,
            shipment?.scheduled_delivery_date
          ),
        }}
      >
        {changeToTimeString(shipment?.schedule_viability_window) ||
          '--hr --min'}
      </Tag>
    </Tooltip>
  );
};

export default ShipmentVitals_CT;
